import { useEffect, useState } from "react";
import "./Dashboard.css";
import "./FlightDetails.css"; /* for .fd-btn family */
import Loader from "./Loader";
import { doc, getDoc, collection, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, useLocation } from "react-router-dom";

/* ── Inline SVG icon set (matches fd-* design system) ── */
const Icon = {
  plane: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2c-.8.2-1.3.8-1.1 1.7l2.7 4.5c.3.5 1 .6 1.5.2L7.5 10l3.5 8.5c.2.5.8.8 1.3.6l4.5-2c.7-.2.8-.9 1-1.9z"/>
    </svg>
  ),
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  arrowSlim: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  sparkle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/>
    </svg>
  ),
};

const STATUS_LABELS = {
  NEW_ORDER: "Request received",
  SEARCHING: "Searching for match",
  MATCHED: "Match confirmed",
  IN_PROGRESS: "Item in transit",
  COMPLETED: "Delivered",
  CANCELLED: "Cancelled",
};

const STATUS_PILL_VARIANT = {
  MATCHED: "is-matched",
  IN_PROGRESS: "is-progress",
  COMPLETED: "",
  CANCELLED: "is-cancelled",
};

/* ── Extract a usable display name from a request doc ── */
const nameFromDoc = (d, role) => {
  if (role === "TRAVELER") {
    const fn = (d.flightDetails?.firstName || "").trim();
    const ln = (d.flightDetails?.lastName || "").trim();
    const full = `${fn} ${ln}`.trim();
    return { full, first: fn || full.split(" ")[0] || "" };
  }
  const full = (d.itemDetails?.senderName || "").trim();
  return { full, first: full.split(" ")[0] || "" };
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [userName, setUserName] = useState({ full: "", first: "" });
  const [activeBooking, setActiveBooking] = useState(null);
  const [noMatchBooking, setNoMatchBooking] = useState(null);
  const [pastBookings, setPastBookings] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const storedPhone =
      localStorage.getItem("PHONE_NUMBER") || location.state?.phoneNumber;
    if (!storedPhone) {
      navigate("/login");
      return;
    }
    setPhone(storedPhone);
    loadBookings(storedPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBookings = async (ph) => {
    try {
      const past = [];
      let active = null;
      let noMatch = null;
      let nameCandidate = { full: "", first: "" };
      const noteName = (d, role) => {
        if (nameCandidate.full) return;
        const n = nameFromDoc(d, role);
        if (n.full) nameCandidate = n;
      };

      // When a request ended (completed/cancelled) — used to date & sort the
      // Past deliveries list. Falls back gracefully for older docs.
      const endAtOf = (d) =>
        d.statusTimestamps?.COMPLETED || d.statusTimestamps?.CANCELLED ||
        d.cancelledAt || d.statusChangedAt || d.createdAt || "";
      const fmtDay = (iso) => {
        if (!iso) return "";
        const dt = new Date(iso);
        return isNaN(dt) ? iso : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
      };

      const buildEntry = (d, role, path) => {
        const rawDate = role === "TRAVELER" ? (d.flightDetails?.travelDate || "") : (d.itemDetails?.sendingDate || "");
        const endAt = endAtOf(d);
        return {
          role,
          path,
          id: d.uniqueKey || "",
          from: d.from?.city || "—",
          to: d.to?.city || "—",
          date: rawDate,
          endAt,                                   // raw, for sorting
          endDate: endAt ? fmtDay(endAt) : rawDate, // display date for past cards
          item: role === "SENDER" ? (d.itemDetails?.itemName || "") : "",
          createdAt: d.createdAt || "",
          status: d.status || (d.requestStatus === "MATCH_FOUND" ? "MATCHED" : d.requestStatus) || "NEW_ORDER",
        };
      };

      const bucket = (d, entry) => {
        if (d.status === "COMPLETED" || d.LastMileStatus === "Completed" || d.status === "CANCELLED") past.push(entry);
        else if (d.status === "NO_MATCH") { if (!noMatch) noMatch = entry; }
        else if (!active) active = entry;
      };

      // All four reads fire together instead of queueing behind each other.
      // This runs right after login, so it used to pay four sequential round
      // trips plus the Firestore connection handshake — the dashboard sat on
      // its spinner for the whole chain. Read count and billing are unchanged:
      // the legacy single-doc lookups still happen only when a collection is
      // empty, and Promise.all over nulls makes no network call.
      const [travelerReqsSnap, senderReqsSnap] = await Promise.all([
        getDocs(collection(db, "users", ph, "TravelerRequests")),
        getDocs(collection(db, "users", ph, "SenderRequests")),
      ]);
      const [travelerDetailsSnap, senderDetailsSnap] = await Promise.all([
        travelerReqsSnap.empty ? getDoc(doc(db, "users", ph, "Traveler", "details")) : null,
        senderReqsSnap.empty ? getDoc(doc(db, "users", ph, "Sender", "details")) : null,
      ]);

      // --- Traveler ---
      if (!travelerReqsSnap.empty) {
        const docs = travelerReqsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.forEach((d) => {
          noteName(d, "TRAVELER");
          bucket(d, buildEntry(d, "TRAVELER", ["users", ph, "TravelerRequests", d.id]));
        });
      } else {
        if (travelerDetailsSnap && travelerDetailsSnap.exists()) {
          const d = travelerDetailsSnap.data();
          noteName(d, "TRAVELER");
          bucket(d, buildEntry(d, "TRAVELER", ["users", ph, "Traveler", "details"]));
        }
      }

      // --- Sender ---
      if (!senderReqsSnap.empty) {
        const docs = senderReqsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.forEach((d) => {
          noteName(d, "SENDER");
          bucket(d, buildEntry(d, "SENDER", ["users", ph, "SenderRequests", d.id]));
        });
      } else {
        if (senderDetailsSnap && senderDetailsSnap.exists()) {
          const d = senderDetailsSnap.data();
          noteName(d, "SENDER");
          bucket(d, buildEntry(d, "SENDER", ["users", ph, "Sender", "details"]));
        }
      }

      // Latest-ended first.
      past.sort((a, b) => String(b.endAt).localeCompare(String(a.endAt)));

      setUserName(nameCandidate);
      setActiveBooking(active);
      setNoMatchBooking(noMatch);
      setPastBookings(past);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Keep looking — reopen a NO_MATCH request so admin searches again
  const handleKeepLooking = async (booking) => {
    if (!booking?.path) return;
    setActionLoading(true);
    try {
      await updateDoc(doc(db, ...booking.path), { status: "SEARCHING" });
      await loadBookings(getPhone());
    } catch (err) {
      console.error("Keep looking failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel a NO_MATCH request and start fresh
  const handleCancelAndNew = async (booking) => {
    if (!booking?.path) return;
    setActionLoading(true);
    try {
      await updateDoc(doc(db, ...booking.path), { status: "CANCELLED", cancelledAt: new Date().toISOString() });
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setActionLoading(false);
      navigate("/selection", { state: { phoneNumber: getPhone() } });
    }
  };

  const getPhone = () => phone || localStorage.getItem("PHONE_NUMBER") || "";

  const goToWaitlist = (booking) => {
    const path = booking.role === "TRAVELER" ? "/traveler-waitlist" : "/sender-waitlist";
    navigate(path, { state: { phoneNumber: getPhone(), role: booking.role } });
  };

  const avatarInitial =
    (userName.first || userName.full || "").trim().charAt(0) ||
    (phone || "").trim().slice(-2, -1) ||
    "T";
  const greeting = userName.first
    ? `Welcome back, ${userName.first}`
    : "Welcome back";

  if (loading) return <Loader />;

  return (
    <div className="dashboard-page">
      <div className="dashboard-card">

        {/* HEADER */}
        <div className="dash-header">
          <div className="dash-avatar" aria-hidden>{avatarInitial.toUpperCase()}</div>
          <div className="dash-header-text">
            <h2 className="dash-greeting" title={userName.full || undefined}>{greeting}</h2>
            <p className="dash-phone">+91 {phone}</p>
          </div>
        </div>

        {/* ACTIVE BOOKING */}
        {activeBooking && (
          <div className="dash-section">
            <h4 className="dash-section-title">Active delivery</h4>
            <div className="dash-active-card">
              <div className="dash-active-head">
                <span className="dash-active-badge">
                  <span className="dash-active-badge-icon" aria-hidden>
                    {activeBooking.role === "TRAVELER" ? Icon.plane : Icon.package}
                  </span>
                  {activeBooking.role === "TRAVELER" ? "Traveller" : "Sender"}
                </span>
                <span className={`dash-status-pill ${STATUS_PILL_VARIANT[activeBooking.status] || ""}`}>
                  <span className="dash-status-pill-dot" aria-hidden></span>
                  {STATUS_LABELS[activeBooking.status] || activeBooking.status}
                </span>
              </div>

              <div className="dash-route">
                <span>{activeBooking.from}</span>
                <span className="dash-route-arrow" aria-hidden>{Icon.arrowSlim}</span>
                <span>{activeBooking.to}</span>
              </div>

              <button
                type="button"
                className="fd-btn fd-btn--primary fd-btn--block"
                onClick={() => goToWaitlist(activeBooking)}
              >
                View status
                <span className="fd-btn-icon" aria-hidden>{Icon.arrowRight}</span>
              </button>
            </div>
          </div>
        )}

        {/* NO MATCH FOUND — admin marked, needs user decision */}
        {noMatchBooking && (
          <div className="dash-section">
            <h4 className="dash-section-title">Needs your attention</h4>
            <div className="dash-active-card" style={{ borderColor: "#fcd34d", background: "#fffbeb" }}>
              <div className="dash-active-head">
                <span className="dash-active-badge">
                  <span className="dash-active-badge-icon" aria-hidden>
                    {noMatchBooking.role === "TRAVELER" ? Icon.plane : Icon.package}
                  </span>
                  {noMatchBooking.role === "TRAVELER" ? "Traveller" : "Sender"}
                </span>
                <span className="dash-status-pill" style={{ background: "#fef3c7", color: "#b45309", borderColor: "rgba(180,83,9,0.2)" }}>
                  <span className="dash-status-pill-dot" aria-hidden></span>
                  No match found
                </span>
              </div>

              <div className="dash-route">
                <span>{noMatchBooking.from}</span>
                <span className="dash-route-arrow" aria-hidden>{Icon.arrowSlim}</span>
                <span>{noMatchBooking.to}</span>
              </div>

              <p style={{ fontSize: "0.84rem", color: "#92400e", lineHeight: 1.6, margin: "4px 0 14px" }}>
                {noMatchBooking.role === "TRAVELER"
                  ? "We couldn't find a sender on your route and dates yet. Want us to keep looking, or cancel and start fresh?"
                  : "We couldn't find a verified traveller on your route yet. Want us to keep looking, or cancel and start fresh?"}
              </p>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="fd-btn fd-btn--primary"
                  style={{ flex: 1, minWidth: 130 }}
                  onClick={() => handleKeepLooking(noMatchBooking)}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Please wait…" : "Keep looking"}
                </button>
                <button
                  type="button"
                  className="fd-btn"
                  style={{ flex: 1, minWidth: 130, background: "#fff", color: "#dc2626", border: "1.5px solid #fca5a5" }}
                  onClick={() => handleCancelAndNew(noMatchBooking)}
                  disabled={actionLoading}
                >
                  Cancel &amp; start new
                </button>
              </div>
            </div>
          </div>
        )}

        {/* NEW REQUEST — only when nothing active or awaiting decision */}
        {!activeBooking && !noMatchBooking && (
          <div className="dash-new-section">
            <div className="dash-new-icon" aria-hidden>{Icon.sparkle}</div>
            <h3>Ready for another delivery?</h3>
            <p>Your last delivery is complete. Start a new request in under a minute.</p>
            <button
              type="button"
              className="fd-btn fd-btn--primary fd-btn--block"
              onClick={() => navigate("/selection", { state: { phoneNumber: getPhone() } })}
              style={{ maxWidth: 360 }}
            >
              <span className="fd-btn-icon" aria-hidden>{Icon.plus}</span>
              Start new request
            </button>
          </div>
        )}

        {/* PAST BOOKINGS */}
        {pastBookings.length > 0 && (
          <div className="dash-section">
            <h4 className="dash-section-title">Past deliveries</h4>
            <div className="dash-past-list">
              {pastBookings.map((b, i) => (
                <div key={i} className="dash-past-card">
                  <div className="dash-past-top">
                    <span className="dash-past-role">
                      <span className="dash-past-role-icon" aria-hidden>
                        {b.role === "TRAVELER" ? Icon.plane : Icon.package}
                      </span>
                      {b.role === "TRAVELER" ? "Traveller" : "Sender"}
                    </span>
                    <span
                      className="dash-past-status"
                      style={b.status === "CANCELLED" ? { color: "#dc2626", background: "#fff2f2", borderColor: "rgba(220,38,38,0.2)" } : {}}
                    >
                      <span className="dash-past-status-icon" aria-hidden style={b.status === "CANCELLED" ? { color: "#dc2626" } : {}}>
                        {b.status === "CANCELLED" ? "✕" : Icon.check}
                      </span>
                      {b.status === "CANCELLED" ? "Cancelled" : "Completed"}
                    </span>
                  </div>

                  <div className="dash-past-route">{b.from} → {b.to}</div>

                  <div className="dash-past-meta">
                    {b.id && <span className="dash-past-id">TX-{b.id}</span>}
                    {b.id && (b.endDate || b.item) && <span className="dash-past-dot">·</span>}
                    {b.item && <span>{b.item}</span>}
                    {b.item && b.endDate && <span className="dash-past-dot">·</span>}
                    {b.endDate && <span>{b.endDate}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="dash-footer-note">
          For queries, use the Help &amp; Support button below.
        </p>
      </div>

      <footer className="dash-footer">
        © {new Date().getFullYear()} TurantX Solutions Pvt Ltd
      </footer>
    </div>
  );
}
